import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import CustomerList from "@/pages/admin/customers/components/CustomerList";
import CustomerHealthBoard from "@/pages/admin/customers/components/CustomerHealthBoard";
import FollowUpBoard from "@/pages/admin/customers/components/FollowUpBoard";
import { useCustomers } from "@/pages/admin/customers/hooks/useCustomers";
import { logExportTask } from "@/lib/exportTaskLog";

interface Notice {
  text: string;
  tone: "success" | "error";
}

export default function CustomersPage() {
  const { customers, staff, loading, error, reload, setStatus, setFollowUp, bulkAssign, lastRemindedAt, recentPushes, lastSinglePushAt, pushRenewalReminders } =
    useCustomers();
  const [notice, setNotice] = useState<Notice | null>(null);

  const notify = useCallback((text: string, tone: "success" | "error" = "success") => {
    setNotice({ text, tone });
    window.setTimeout(() => setNotice(null), 4000);
  }, []);

  const handleBulkAssign = useCallback(
    async (userIds: string[], assignee: string) => {
      try {
        const result = await bulkAssign(userIds, assignee);
        notify(
          result.assigned > 0
            ? `已将 ${result.assigned} 位客户指派给「${assignee}」${result.skipped > 0 ? `，${result.skipped} 位无需重复指派` : ""}`
            : `「${assignee}」名下已是这组客户，无需重复指派`,
          result.assigned > 0 ? "success" : "error",
        );
        return result;
      } catch (err) {
        notify(err instanceof Error ? err.message : "批量指派失败，请稍后重试", "error");
        return { assigned: 0, skipped: 0 };
      }
    },
    [bulkAssign, notify],
  );

  const handlePushReminders = useCallback(
    async (windowDays: number, userId?: string) => {
      try {
        const result = await pushRenewalReminders(windowDays, userId);
        if (userId) {
          // 单独推送：只针对某位客户补推一次，不计入报表中心的批量推送留痕（服务端仍会周期去重）。
          const emailText = result.emailConfigured
            ? `，邮件成功 ${result.emailsSent}${result.emailsFailed > 0 ? ` / 失败 ${result.emailsFailed}` : ""}`
            : "，邮件未配置仅站内通知";
          if (result.pushed > 0) {
            notify(`已单独推送 1 位客户的续期提醒（共 ${result.pushedLicenses} 张许可证）${emailText}`, "success");
          } else if (result.skipped > 0) {
            notify(`该客户在 ${windowDays} 天周期内已提醒过，本次自动去重跳过`, "error");
          } else {
            notify(`该客户近 ${windowDays} 天内没有即将到期的许可证，无需推送`, "error");
          }
          return result;
        }
        if (result.pushed > 0) {
          // 把本次批量推送写入报表中心导出留痕：记录谁在何时向多少人推了提醒，便于统一审计。
          void logExportTask({
            reportKey: "renewal-reminder-push",
            reportName: "到期提醒推送（站内通知 + 邮件）",
            category: "客户",
            rowCount: result.pushed,
            windowLabel: `${windowDays} 天窗口 · ${result.pushedLicenses} 张许可证`,
          });
          const skipText = result.skipped > 0 ? `，跳过 ${result.skipped} 位本周期内已提醒` : "";
          const emailText = result.emailConfigured
            ? `，邮件成功 ${result.emailsSent}${result.emailsFailed > 0 ? ` / 失败 ${result.emailsFailed}` : ""}`
            : "，邮件未配置仅站内通知";
          notify(
            `已向 ${result.pushed} 位即将到期客户推送续期提醒（共 ${result.pushedLicenses} 张许可证）${skipText}${emailText}`,
            "success",
          );
        } else if (result.skipped > 0) {
          notify(
            `近 ${windowDays} 天内到期的客户本周期内均已提醒过，本次无新增推送（跳过 ${result.skipped} 位）`,
            "error",
          );
        } else {
          notify(`近 ${windowDays} 天内没有即将到期的客户，无需推送`, "error");
        }
        return result;
      } catch (err) {
        notify(err instanceof Error ? err.message : "推送续期提醒失败，请稍后重试", "error");
        return {
          pushed: 0,
          skipped: 0,
          licenses: 0,
          pushedLicenses: 0,
          windowDays,
          emailsSent: 0,
          emailsFailed: 0,
          emailConfigured: false,
        };
      }
    },
    [pushRenewalReminders, notify],
  );

  const stats = useMemo(() => {
    const registered = customers.filter((customer) => customer.registered).length;
    const suspended = customers.filter((customer) => customer.status === "suspended").length;
    const pending = customers.reduce((sum, customer) => sum + customer.pendingCount, 0);
    return { total: customers.length, registered, suspended, pending };
  }, [customers]);

  const cards = [
    { label: "客户总数", value: stats.total, icon: "ri-group-line", tone: "primary" },
    { label: "已注册账号", value: stats.registered, icon: "ri-user-star-line", tone: "primary" },
    { label: "待审批申请", value: stats.pending, icon: "ri-time-line", tone: "accent" },
    { label: "已停用账号", value: stats.suspended, icon: "ri-user-forbid-line", tone: "secondary" },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-700",
    secondary: "bg-secondary-100 text-secondary-900",
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <AppHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="cursor-pointer transition-colors hover:text-foreground-950">
                首页
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <Link to="/admin" className="cursor-pointer transition-colors hover:text-foreground-950">
                管理后台
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <span className="text-foreground-800">客户管理</span>
            </nav>
            <div className="mt-4 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                  <span className="flex h-3 w-3 items-center justify-center">
                    <i className="ri-group-line" />
                  </span>
                  客户全景视图
                </span>
                <h1 className="mt-3 font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
                  客户管理
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  按客户聚合其名下的全部授权申请与许可证，标注最近一次操作时间与风险标记（长期无活跃 / 频繁换机），支持「风险客户 / 跟进中」筛选与按跟进负责人筛选，风险名单与客服跟进清单导出，顶部提供按风险标记分组的跟进进度看板、负责人工作量统计（点击可直接筛出该负责人的跟进客户），以及「负责人 × 风险标记」组合视角，一眼看清每位负责人名下有多少风险客户待处理；看板支持一键导出周报，每张风险卡片还可一键把整组风险客户批量指派给指定负责人，跟进弹窗会展示负责人当前在跟客户数，避免分配失衡，并可停用或恢复客户账号。
                </p>
              </div>
              <Link
                to="/admin"
                className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100 md:w-auto"
              >
                <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                  <i className="ri-arrow-left-line" />
                </span>
                返回管理后台
              </Link>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map((card) => (
              <div key={card.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-foreground-600">{card.label}</span>
                  <span className={`flex h-8 w-8 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
                    <i className={`${card.icon} text-base`} />
                  </span>
                </div>
                <p className="mt-2 font-heading text-2xl font-semibold text-foreground-950">
                  {loading ? "--" : card.value}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <CustomerHealthBoard
              customers={customers}
              loading={loading}
              lastRemindedAt={lastRemindedAt}
              recentPushes={recentPushes}
              onPushReminders={handlePushReminders}
            />
          </div>

          <div className="mt-8">
            <FollowUpBoard
              customers={customers}
              loading={loading}
              staff={staff}
              onBulkAssign={handleBulkAssign}
            />
          </div>

          <div className="mt-8">
            <CustomerList
              customers={customers}
              staff={staff}
              loading={loading}
              error={error}
              lastSinglePushAt={lastSinglePushAt}
              onRetry={reload}
              onSetStatus={setStatus}
              onSetFollowUp={setFollowUp}
              notify={notify}
            />
          </div>
        </section>
      </main>

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 px-4">
          <div
            className={`lh-fade-up flex items-center gap-2.5 rounded-lg px-4 py-3 text-sm shadow-sm ${
              notice.tone === "success" ? "bg-primary-600 text-background-50" : "bg-accent-600 text-background-50"
            }`}
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={notice.tone === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
            </span>
            {notice.text}
          </div>
        </div>
      )}

      <AppFooter />
    </div>
  );
}